import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ApiFullTaxReportService } from '../../../../../core/services/api/report/api-fulltax-report.service';
import { MapperFullTaxReportService } from '../../../../../core/mappers/report/mapper-fulltax-report.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { VatSettingService } from '../../../../../core/services/vat-setting.service';
import { FullTaxReportById, isFullTaxInvoiceEditable } from '../../../../../core/models/report/fulltax-report.model';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-full-tax-report-edit',
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-full-tax-report-edit.component.html',
  styleUrl: './modal-full-tax-report-edit.component.css'
})
export class ModalFullTaxReportEditComponent implements OnInit {
  @Input() invoiceId!: number;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  private readonly apiService = inject(ApiFullTaxReportService);
  private readonly mapper = inject(MapperFullTaxReportService);
  private readonly toast = inject(ToastService);
  readonly vatSetting = inject(VatSettingService);
  private readonly cookieService = inject(CookieService);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly router = inject(Router);

  isLoading = false;
  formData: FullTaxReportById | null = null;

  get isReadOnly(): boolean {
    return !this.formData || !isFullTaxInvoiceEditable(this.formData.status);
  }

  ngOnInit(): void {
    this.loadInvoiceDetails();
  }

  loadInvoiceDetails(): void {
    this.isLoading = true;
    this.apiService.getFullTaxInvoiceById(this.invoiceId).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapFullTaxByIdFromDto(res);
        this.isLoading = false;
        if (this.isReadOnly) {
          this.toast.error('ใบกำกับภาษีนี้ยืนยันแล้ว ไม่สามารถแก้ไขได้');
          this.closeModal();
        }
      },
      error: () => {
        this.toast.error('ไม่สามารถดึงข้อมูลรายละเอียดใบกำกับภาษีได้');
        this.isLoading = false;
        this.closeModal();
      }
    });
  }

  saveData(): void {
    if (!this.formData || this.isReadOnly) return;

    const userId = Number(this.cookieService.get('user_id')) || 0;
    const menuId = this.permissionService.getMenuId(this.router.url);

    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    if (!this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }

    this.isLoading = true;
    const payload = this.mapper.mapUpdatePayloadFromForm(this.formData, userId, menuId);

    this.apiService.updateFullTaxInvoice(payload).subscribe({
      next: (res) => {
        const result = this.mapper.mapActionResponse(res);
        this.isLoading = false;
        if (result.isSuccess) {
          this.toast.success('บันทึกข้อมูลเรียบร้อยแล้ว');
          this.saveSuccess.emit();
          return;
        }
        this.toast.error(result.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
      },
      error: (err) => {
        this.isLoading = false;
        const message = err?.error?.message ?? err?.error?.Message;
        this.toast.error(message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
      }
    });
  }

  closeModal(): void {
    this.close.emit();
  }

  get canShowActivityHistory(): boolean {
    return this.invoiceId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
